import {
  BenefitState,
  type BundlePartView,
  type ClaimPolicyViewWithRedemption,
  type HeldBenefit,
  PARTNER_TARGET_KIND,
  PartKinds,
  type PartnerRedemptionView,
} from '@/services/myBenefits'

/**
 * One way to spend a benefit in the app: a kind of place, and the grant a claim there spends.
 *
 * For a single benefit the grant is its own and there is one option per kind of place it names.
 * For a bundle made of several benefits the bundle itself is never claimed — each part is — so the
 * options are its claimable parts' own, each carrying the part's grant. The claim picker's tabs are
 * these options, and the active one decides both which listing is asked and which grant it is
 * asked for and claimed with.
 */
export interface ClaimOption {
  /** Unique within one benefit's options; what a tab is keyed and selected by. */
  key: string
  /** The grant listed and claimed: the benefit's own, or a part's. */
  grantId: number
  policy: ClaimPolicyViewWithRedemption
  /**
   * What the tab says. For a single benefit, the server's name for the kind of place. For a part,
   * the part's own name — see {@link namesKind} for when the kind goes beside it.
   */
  label: string
  /**
   * A part that can go to more than one kind of place: its tabs need the kind beside the part's name
   * (`policy.targetKindLabel`), or two tabs would read the same.
   */
  namesKind: boolean
  /** The held benefit the option spends — the part's own for a part. */
  benefit: HeldBenefit
}

/**
 * The kinds of place a benefit can be claimed at in the app, one per kind.
 *
 * Policies arrive one per (type, kind), so a benefit covering two types spendable at clubs repeats
 * `club`. A blank kind names nowhere and a partner is never claimed in the app (the member follows
 * the partner's link), so neither is a place to pick. Nothing here supplies a kind a benefit did not
 * name: one with none is not claimable in the app, and is not shown at all — see {@link isShown}.
 */
export const usablePolicies = (benefit: HeldBenefit): ClaimPolicyViewWithRedemption[] => {
  const policies = (benefit.claimPolicies ?? []).filter(
    (policy) => !!policy.targetKind?.trim() && policy.targetKind !== PARTNER_TARGET_KIND
  )
  return policies.filter(
    (policy, index) => policies.findIndex((other) => other.targetKind === policy.targetKind) === index
  )
}

/** Every leaf of a part tree, in authored order. */
export const partLeaves = (part: BundlePartView): BundlePartView[] =>
  part.kind === PartKinds.Benefit ? [part] : (part.children ?? []).flatMap(partLeaves)

/** The leaves that can be claimed right now, each with its own held benefit. */
const claimableLeaves = (part: BundlePartView): Array<BundlePartView & { benefit: HeldBenefit }> =>
  partLeaves(part).filter((leaf): leaf is BundlePartView & { benefit: HeldBenefit } => leaf.claimable && !!leaf.benefit)

/**
 * The ways [benefit] can be claimed in the app right now; empty when there are none.
 *
 * A bundle's options are its claimable parts', and a part that names no usable kind contributes
 * nothing rather than borrowing one.
 */
export const claimOptions = (benefit: HeldBenefit): ClaimOption[] => {
  if (benefit.parts) {
    return claimableLeaves(benefit.parts).flatMap((leaf) => {
      const policies = usablePolicies(leaf.benefit)
      return policies.map((policy) => ({
        key: `${leaf.benefit.grantId}:${policy.targetKind}`,
        grantId: leaf.benefit.grantId,
        policy,
        label: leaf.label,
        namesKind: policies.length > 1,
        benefit: leaf.benefit,
      }))
    })
  }
  return usablePolicies(benefit).map((policy) => ({
    key: `${benefit.grantId}:${policy.targetKind}`,
    grantId: benefit.grantId,
    policy,
    label: policy.targetKindLabel,
    namesKind: false,
    benefit,
  }))
}

/**
 * Partners where [benefit] can be used — for a bundle, its own and its claimable parts'.
 *
 * Like claiming, using a part at a partner spends that part, so a part that can no longer be
 * started offers no partner either.
 */
export const partnersOf = (benefit: HeldBenefit): PartnerRedemptionView[] =>
  benefit.parts
    ? [...benefit.partners, ...claimableLeaves(benefit.parts).flatMap((leaf) => leaf.benefit.partners)].filter(
        (partner, index, all) => all.findIndex((other) => other.partnerUrn === partner.partnerUrn) === index
      )
    : benefit.partners

/**
 * Whether a benefit belongs in the member's list at all.
 *
 * A benefit still to spend is shown only when there is somewhere to spend it: a kind of place it
 * names, a partner, or — for a bundle — a part that has one. Anything else is malformed or no longer
 * spendable, and showing it would offer a claim with nowhere it could go; it is left out rather than
 * guessed at. One already claimed, used or expired is always shown: it is a record of what happened.
 */
export const isShown = (benefit: HeldBenefit): boolean =>
  benefit.state !== BenefitState.Available || claimOptions(benefit).length > 0 || partnersOf(benefit).length > 0

/** The grants that are parts of a bundle in [benefits] — shown inside their bundle, not on their own. */
export const partGrantIds = (benefits: HeldBenefit[]): Set<number> => {
  const ids = new Set<number>()
  benefits.forEach((benefit) => {
    if (!benefit.parts) return
    partLeaves(benefit.parts).forEach((leaf) => {
      if (leaf.benefit) ids.add(leaf.benefit.grantId)
    })
  })
  return ids
}

/**
 * The member's benefits as they are listed: a bundle's parts inside it rather than beside it, and
 * nothing that could not be spent — see {@link isShown}.
 */
export const visibleBenefits = (benefits: HeldBenefit[]): HeldBenefit[] => {
  const insideABundle = partGrantIds(benefits)
  return benefits.filter((benefit) => !insideABundle.has(benefit.grantId) && isShown(benefit))
}

/**
 * The benefits that can be claimed in the app right now, oldest first — what a link that names no
 * benefit, or a coach's or club's own page, chooses from. Only listed benefits with an option: a
 * bundle's parts are reached through their bundle.
 */
export const claimableBenefits = (benefits: HeldBenefit[]): HeldBenefit[] =>
  visibleBenefits(benefits)
    .filter((benefit) => benefit.state === BenefitState.Available && claimOptions(benefit).length > 0)
    .sort((a, b) => a.expires.localeCompare(b.expires))

/**
 * The benefit to claim at a place of [targetKind] — a coach's or club's own page — from
 * [benefits]: the oldest claimable one with an option of that kind, as the benefit that option
 * spends (for a bundle, the part's own). Null when nothing held can be claimed there; never a
 * benefit that names some other kind of place.
 */
export const claimableAt = (benefits: HeldBenefit[], targetKind: string): HeldBenefit | null =>
  claimableBenefits(benefits)
    .flatMap(claimOptions)
    .find((option) => option.policy.targetKind === targetKind)?.benefit ?? null

/**
 * The benefit a claim link names, as the picker should open it: a listed benefit by its own grant,
 * or the bundle a part belongs to when the link names a part. Undefined when it names nothing that
 * can be claimed here.
 */
export const claimTarget = (benefits: HeldBenefit[], grantId: number): HeldBenefit | undefined => {
  const claimable = claimableBenefits(benefits)
  return (
    claimable.find((benefit) => benefit.grantId === grantId) ??
    claimable.find(
      (benefit) => !!benefit.parts && partLeaves(benefit.parts).some((leaf) => leaf.benefit?.grantId === grantId)
    )
  )
}
